import { useEffect, useRef, useState } from "react";
import { TailSpin } from "react-loader-spinner";
import { Modal } from "../../../../ui/modal/modal.tsx";
import { Button } from "../../../../ui/button/button.tsx";
import {
  TimesheetEntryForm,
  type TimesheetEntryFormValues,
} from "../../forms/timesheet-entry-form/timesheet-entry-form.tsx";
import { useTimesheetOptions } from "../../hooks/use-timesheet-options.ts";
import type { TimesheetEntry } from "../../types.ts";
import { isRejectedEditableEntry } from "../../utils/timesheet-entry-flags.ts";
import modalForms from "../../../../features/my-profile/forms/modal-forms.module.css";
import styles from "./edit-entry-modal.module.css";

const FORM_ID = "timesheet-edit-entry-form";

type Props = {
  entry: TimesheetEntry;
  selectedDate: Date;
  onClose: () => void;
  onSave: (updated: TimesheetEntry) => void;
};

export function EditEntryModal({ entry, selectedDate, onClose, onSave }: Props) {
  const { projects, categories, phases, projectWorkstreamsMap, loading, categoriesLoading, loadProjectCategories } =
    useTimesheetOptions(selectedDate);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [hasCategory, setHasCategory] = useState(
    entry.categoryId != null || Boolean(entry.category?.trim()),
  );
  const [loadedEntryOptionsKey, setLoadedEntryOptionsKey] = useState("");
  const requestedEntryOptionsKey = useRef("");
  const lockProject = isRejectedEditableEntry(entry);

  const resolvedProjectValue = entry.titleType === "OTHERS"
    ? "MISCELLANEOUS"
    : projects.find((p) => p.label === entry.project)?.value ?? "";

  const resolvedWorkstreamValue = entry.workStreamId != null
    ? String(entry.workStreamId)
    : String(
        projectWorkstreamsMap[resolvedProjectValue]?.find(
          (stream) => stream.workStream === entry.workStream,
        )?.workStreamId ?? "",
      );

  const entryOptionsKey = resolvedProjectValue
    ? `${resolvedProjectValue}:${resolvedWorkstreamValue || "PROJECT"}`
    : "";

  useEffect(() => {
    setIsFormDirty(false);
    setLoadedEntryOptionsKey("");
    requestedEntryOptionsKey.current = "";
  }, [entry.id]);

  useEffect(() => {
    if (loading || !resolvedProjectValue || requestedEntryOptionsKey.current === entryOptionsKey) return;
    requestedEntryOptionsKey.current = entryOptionsKey;
    const request = resolvedWorkstreamValue
      ? loadProjectCategories(resolvedProjectValue, resolvedWorkstreamValue)
      : loadProjectCategories(resolvedProjectValue);
    void Promise.resolve(request).finally(() => setLoadedEntryOptionsKey(entryOptionsKey));
  }, [
    entryOptionsKey,
    loadProjectCategories,
    loading,
    resolvedProjectValue,
    resolvedWorkstreamValue,
  ]);

  const resolvedCategory = categories.find(
    (category) =>
      (entry.categoryId != null && category.value === String(entry.categoryId)) ||
      category.label.trim().toLowerCase() === String(entry.category || "").trim().toLowerCase(),
  );

  const resolvedCategoryValue = resolvedCategory?.value ?? "";

  useEffect(() => {
    if (!entryOptionsKey || loadedEntryOptionsKey !== entryOptionsKey) return;
    setHasCategory(Boolean(resolvedCategoryValue));
  }, [entryOptionsKey, loadedEntryOptionsKey, resolvedCategoryValue]);

  const initial: TimesheetEntryFormValues = {
    project: resolvedProjectValue,
    workStreamId: resolvedWorkstreamValue,
    phase: entry.phaseId != null ? String(entry.phaseId) : entry.phase ?? "",
    category: resolvedCategoryValue,
    description: entry.description,
    hours: entry.hours.toString(),
    minutes: entry.minutes,
    status: entry.status,
  };

  const handleSave = (values: TimesheetEntryFormValues) => {
    if (!values.category?.trim()) return;
    const selectedCategory = categories.find((category) => category.value === values.category);
    const selectedPhase = phases.find((phase) => phase.value === values.phase);
    const isModified =
      values.project !== initial.project ||
      values.workStreamId !== initial.workStreamId ||
      values.phase !== initial.phase ||
      values.category !== initial.category ||
      values.description !== initial.description ||
      Number(values.hours) !== Number(initial.hours) ||
      values.minutes !== initial.minutes;
    if (lockProject) {
      const selectedWorkStream = projectWorkstreamsMap[values.project]?.find(
        (stream) => String(stream.workStreamId) === values.workStreamId,
      );
      onSave({
        ...entry,
        category: selectedCategory?.label ?? entry.category,
        categoryId: Number(values.category) || entry.categoryId || null,
        phase: selectedPhase?.id ? null : values.phase || null,
        phaseId: selectedPhase?.id ?? null,
        phaseCode: selectedPhase?.code ?? (values.phase || null),
        phaseName: selectedPhase?.label ?? null,
        description: values.description,
        hours: Number(values.hours) || 0,
        minutes: values.minutes,
        status: values.status,
        workStream: selectedWorkStream?.workStream,
        workStreamId: selectedWorkStream?.workStreamId ?? null,
        allocationId: null,
        isModified,
      });
      onClose();
      return;
    }
    const isMiscellaneous = values.project === "MISCELLANEOUS";
    const selectedProject = projects.find((p) => p.value === values.project);
    const selectedWorkStream = projectWorkstreamsMap[values.project]?.find(
      (stream) => String(stream.workStreamId) === values.workStreamId,
    );
    onSave({
      ...entry,
      projectId: isMiscellaneous ? null : Number(values.project),
      project: selectedProject?.label ?? entry.project,
      category: selectedCategory?.label ?? values.category,
      categoryId: isMiscellaneous ? null : Number(values.category),
      phase: isMiscellaneous || selectedPhase?.id ? null : values.phase || null,
      phaseId: isMiscellaneous ? null : selectedPhase?.id ?? null,
      phaseCode: isMiscellaneous ? null : (selectedPhase?.code ?? (values.phase || null)),
      phaseName: isMiscellaneous ? null : selectedPhase?.label ?? null,
      description: values.description,
      hours: Number(values.hours) || 0,
      minutes: values.minutes,
      status: values.status,
      workStream: selectedWorkStream?.workStream,
      workStreamId: selectedWorkStream?.workStreamId ?? null,
      titleType: isMiscellaneous ? "OTHERS" : "PROJECT",
      allocationId: null,
      isModified,
    });
    onClose();
  };

  return (
    <Modal
      open
      size="xl"
      onClose={onClose}
      title="Edit Timesheet Entry"
      footer={
        <div className={modalForms.footerActions}>
          <Button type="button" variant="muted-outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="accent"
            form={FORM_ID}
            disabled={loading || !isFormDirty || !hasCategory}
          >
            Save Changes
          </Button>
        </div>
      }
    >
      {loading || (entryOptionsKey !== "" && loadedEntryOptionsKey !== entryOptionsKey) ? (
        <div className={styles.loaderWrap} role="status" aria-live="polite">
          <TailSpin
            height="56"
            width="56"
            color="var(--primary-color)"
            visible
          />
          <p className={styles.loaderText}>Loading form…</p>
        </div>
      ) : (
        <TimesheetEntryForm
          formId={FORM_ID}
          initial={initial}
          projects={projects}
          categories={categories}
          phases={phases}
          categoriesLoading={categoriesLoading}
          onProjectChange={loadProjectCategories}
          onWorkstreamChange={loadProjectCategories}
          projectWorkstreamsMap={projectWorkstreamsMap}
          validate={false}
          showAsterisks={false}
          requiredCategory
          onSave={handleSave}
          onDirtyChange={setIsFormDirty}
          onCategoryChange={(category) => setHasCategory(Boolean(category.trim()))}
          disableProjectSelect={lockProject}
        />
      )}
    </Modal>
  );
}
